import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '../../../utils/api'
import { centsToEur } from '../payroll-engine'
import { useAuth } from '../../../hooks/useAuth'
import styles from './PayslipHistoryPage.module.css'

interface PayslipSummary {
  id: string
  employee_id: string
  employee_name: string
  period_year: number
  period_month: number
  gross_salary_cents: number
  net_salary_cents: number
  status: string
  pdf_generated: number
  validated_by: string | null
  validated_at: string | null
  locked_by: string | null
  locked_at: string | null
  created_at: string
}

interface EmployeeOption {
  id: string
  name: string
}

type StatusFilter = 'all' | 'draft' | 'validated' | 'locked'

export function PayslipHistoryPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [payslips, setPayslips] = useState<PayslipSummary[]>([])
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [selectedEmployee, setSelectedEmployee] = useState<string>('all')
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())
  const [selectedStatus, setSelectedStatus] = useState<StatusFilter>('all')
  const [loading, setLoading] = useState(true)
  const [acting, setActing] = useState<string | null>(null)
  const [periodStatus, setPeriodStatus] = useState<string>('open')
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null)

  const isAdmin = user?.role === 'admin' || user?.role === 'owner'

  useEffect(() => {
    loadData()
  }, [selectedEmployee, selectedYear, selectedStatus, selectedMonth])

  async function loadData() {
    setLoading(true)

    if (employees.length === 0) {
      const empRes = await apiFetch<{ employees: EmployeeOption[] }>('/employees?fields=id,name')
      if (empRes.data) setEmployees(empRes.data.employees)
    }

    const params = new URLSearchParams({ year: String(selectedYear) })
    if (selectedEmployee !== 'all') params.set('employee_id', selectedEmployee)
    if (selectedStatus !== 'all') params.set('status', selectedStatus)
    if (selectedMonth !== null) params.set('month', String(selectedMonth))

    const res = await apiFetch<{ payslips: PayslipSummary[]; periodStatus: string | null }>(`/payslips?${params}`)
    if (res.data) {
      setPayslips(res.data.payslips)
      setPeriodStatus(res.data.periodStatus ?? 'open')
    }

    setLoading(false)
  }

  async function transitionPayslip(id: string, action: 'validate' | 'invalidate' | 'lock') {
    setActing(id)
    await apiFetch(`/payslips/${id}/${action}`, { method: 'PUT' })
    setActing(null)
    loadData()
  }

  async function deletePayslip(id: string) {
    if (!confirm(t('history.confirm_delete'))) return
    setActing(id)
    await apiFetch(`/payslips/${id}`, { method: 'DELETE' })
    setActing(null)
    loadData()
  }

  async function lockPeriod() {
    if (!selectedMonth) return
    if (!confirm(t('history.confirm_lock_period', { period: `${monthLabel(selectedMonth)} ${selectedYear}` }))) return
    setActing('period')
    await apiFetch('/payslips/lock-period', {
      method: 'POST',
      body: JSON.stringify({ year: selectedYear, month: selectedMonth }),
    })
    setActing(null)
    loadData()
  }

  const getStatusBadge = (status: string): string => {
    if (status === 'validated') return styles.statusValidated ?? ''
    if (status === 'locked') return styles.statusLocked ?? ''
    return styles.statusDraft ?? ''
  }

  const monthLabel = (month: number): string => {
    const date = new Date(2026, month - 1)
    return date.toLocaleDateString(undefined, { month: 'long' })
  }

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i)
  const months = Array.from({ length: 12 }, (_, i) => i + 1)

  const hasValidatedInPeriod = selectedMonth !== null && payslips.some(s => s.status === 'validated')
  const hasDraftsInPeriod = selectedMonth !== null && payslips.some(s => s.status === 'draft')

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>{t('history.title')}</h1>
        <p className={styles.subtitle}>{t('history.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <select
          className={styles.filterSelect}
          value={selectedEmployee}
          onChange={e => setSelectedEmployee(e.target.value)}
        >
          <option value="all">{t('history.all_employees')}</option>
          {employees.map(emp => (
            <option key={emp.id} value={emp.id}>{emp.name}</option>
          ))}
        </select>

        <select
          className={styles.filterSelect}
          value={selectedYear}
          onChange={e => setSelectedYear(Number(e.target.value))}
        >
          {years.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <select
          className={styles.filterSelect}
          value={selectedMonth ?? ''}
          onChange={e => setSelectedMonth(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">{t('history.all_months')}</option>
          {months.map(m => (
            <option key={m} value={m}>{monthLabel(m)}</option>
          ))}
        </select>

        <select
          className={styles.filterSelect}
          value={selectedStatus}
          onChange={e => setSelectedStatus(e.target.value as StatusFilter)}
        >
          <option value="all">{t('history.all_statuses')}</option>
          <option value="draft">{t('history.status_draft')}</option>
          <option value="validated">{t('history.status_validated')}</option>
          <option value="locked">{t('history.status_locked')}</option>
        </select>
      </div>

      {/* Period actions (admin only) */}
      {isAdmin && selectedMonth !== null && (
        <div className={styles.periodBar}>
          <span className={styles.periodLabel}>
            {monthLabel(selectedMonth)} {selectedYear} — {periodStatus === 'closed' ? t('history.period_closed') : t('history.period_open')}
          </span>
          {periodStatus !== 'closed' && hasValidatedInPeriod && !hasDraftsInPeriod && (
            <button
              className={styles.lockPeriodBtn}
              onClick={lockPeriod}
              disabled={acting === 'period'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
              {t('history.lock_period')}
            </button>
          )}
          {periodStatus !== 'closed' && hasDraftsInPeriod && (
            <span className={styles.periodWarning}>{t('history.drafts_remaining')}</span>
          )}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <p className={styles.loading}>{t('common.loading')}</p>
      ) : payslips.length === 0 ? (
        <div className={styles.empty}>
          <p>{t('history.no_payslips')}</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <div className={styles.tableHeader}>
            <span className={styles.colEmployee}>{t('history.col_employee')}</span>
            <span className={styles.colPeriod}>{t('history.col_period')}</span>
            <span className={styles.colGross}>{t('history.col_gross')}</span>
            <span className={styles.colNet}>{t('history.col_net')}</span>
            <span className={styles.colStatus}>{t('history.col_status')}</span>
            <span className={styles.colActions}></span>
          </div>

          {payslips.map(slip => (
            <div key={slip.id} className={styles.tableRow}>
              <span className={styles.colEmployee}>{slip.employee_name}</span>
              <span className={styles.colPeriod}>
                {monthLabel(slip.period_month)} {slip.period_year}
              </span>
              <span className={styles.colGross}>{centsToEur(slip.gross_salary_cents)} EUR</span>
              <span className={styles.colNet}>{centsToEur(slip.net_salary_cents)} EUR</span>
              <span className={styles.colStatus}>
                <span className={`${styles.badge} ${getStatusBadge(slip.status)}`}>
                  {t(`history.status_${slip.status}`)}
                </span>
              </span>
              <span className={styles.colActions}>
                {acting === slip.id ? (
                  <span className={styles.actingLabel}>{t('common.loading')}</span>
                ) : (
                  <>
                    {/* Draft actions */}
                    {slip.status === 'draft' && (
                      <>
                        <button className={styles.actionBtn} onClick={() => transitionPayslip(slip.id, 'validate')}>
                          {t('history.validate')}
                        </button>
                        {(user?.role === 'owner') && (
                          <button className={styles.actionBtnDanger} onClick={() => deletePayslip(slip.id)}>
                            {t('history.delete')}
                          </button>
                        )}
                      </>
                    )}
                    {/* Validated actions */}
                    {slip.status === 'validated' && (
                      <>
                        <button className={styles.actionBtn}>{t('vault.download')}</button>
                        {isAdmin && (
                          <>
                            <button className={styles.actionBtn} onClick={() => transitionPayslip(slip.id, 'lock')}>
                              {t('history.lock')}
                            </button>
                            <button className={styles.actionBtnDanger} onClick={() => transitionPayslip(slip.id, 'invalidate')}>
                              {t('history.invalidate')}
                            </button>
                          </>
                        )}
                      </>
                    )}
                    {/* Locked actions */}
                    {slip.status === 'locked' && (
                      <button className={styles.actionBtn}>{t('vault.download')}</button>
                    )}
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
